import React, { useContext, useState, useEffect, useRef } from "react";

import NumberFormat, { NumberFormatValues } from "react-number-format";

import { rightChevron } from "../../CalculatorTool/Icons";
import { CalculatorStepsContext } from "../../Store/CalculatorStepsContext";
import { fold } from "fp-ts/lib/Option";
import { pipe } from "fp-ts/lib/pipeable";
import {
  VALIDATION_MUST_ENTER_PREMIUM_PAYMENT,
  VALIDATION_PREMIUM_PAYMENT_OUT_OF_RANGE,
  VALIDATION_MUST_CHOOSE_INVESTMENT_TERM,
  VALIDATION_INVESTMENT_TERM_LENGTH_NOT_ALLOWED,
  validateStep1PremiumPayment,
  combineValidations,
  combineValidationsOnBlur,
  getFirstValidationError,
} from "../../../model/CalculatorInputsValidation";
import { ValidationMessage } from "../ValidationMessage";
import { CalculatorSteps } from "../../../model/CalculatorSteps";

import "../InputSteps.scss";
import { TermLength } from "../../../model/CalculatorInputs";
import {
  ANALYTICS_SCREENS,
  analyticsScreenChangeEventWithInputs,
} from "../../../utils/Analytics";
import { SelectTermLength } from "./SelectTermLength";

export const min = 25000;
export const max = 5000000;

const premiumBlankMessage =
  "Must enter an amount between $25,000 and $5,000,000";
const premiumRangeMessage = "Must be an amount between $25,000 and $5,000,000";

export const premiumValidationMessages = new Map<string, string>([
  [VALIDATION_MUST_ENTER_PREMIUM_PAYMENT, premiumBlankMessage],
  [VALIDATION_PREMIUM_PAYMENT_OUT_OF_RANGE, premiumRangeMessage],
]);

export const investmentTermValidationMessages = new Map<string, string>([
  [VALIDATION_MUST_CHOOSE_INVESTMENT_TERM, "Must choose term length"],
  [
    VALIDATION_INVESTMENT_TERM_LENGTH_NOT_ALLOWED,
    "Must choose valid term length",
  ],
]);

export const Step1EnterPremiumChooseTerm = () => {
  const minValue = (
    <NumberFormat
      prefix="$"
      thousandSeparator=","
      value={Math.round(min)}
      displayType="text"
    />
  );
  const maxValue = (
    <NumberFormat
      prefix="$"
      thousandSeparator=","
      value={Math.round(max)}
      displayType="text"
    />
  );
  const premiumInputId = "rila-calculator-tool-premium";
  const premiumInputLabelId = premiumInputId + "-label";
  const premiumInputDescriptionId = premiumInputId + "-description";

  const termButtonsId = "rila-calculator-tool-term";
  const termButtonsLabelId = termButtonsId + "-label";
  const termButtonsDescriptionId = termButtonsId + "-description";

  const premiumValidationIssues = Array.from(premiumValidationMessages.keys());
  const nextIcon = rightChevron("1x", "white");

  const { steps, setSteps } = useContext(CalculatorStepsContext);

  const inputStepClasses = ["input-step", "input-step-1"];
  if (steps.stepIndex === CalculatorSteps.STEP_1_ENTER_PREMIUM_CHOOSE_TERM) {
    inputStepClasses.push("current-step");
  }

  const initAmountStr = pipe(
    steps.inputs.premiumPayment,
    fold(
      () => "",
      (amount) => amount.toString()
    )
  );
  const [amount, setAmount] = useState<string>(initAmountStr);
  const [dirty, setDirty] = useState<boolean>(false);

  let premiumRef: HTMLInputElement | null = null;
  let termRef: React.RefObject<HTMLSelectElement> =
    useRef<HTMLSelectElement>(null);

  function termSelectGrabFocus(ref: React.RefObject<HTMLSelectElement>) {
    termRef = ref;
  }

  useEffect(() => {
    const grabFocus = async () => {
      pipe(
        steps.firstValidationError,
        fold(
          () => {
            return;
          },
          (errMsg) => {
            if (premiumValidationMessages.has(errMsg)) {
              if (premiumRef !== null) {
                premiumRef.focus();
              }
            } else if (investmentTermValidationMessages) {
              if (termRef.current !== null) {
                termRef.current.focus();
              }
            }
          }
        )
      );
    };
    setTimeout(grabFocus, 25);
  }, [steps.firstValidationError]);

  const handleNext = () => {
    const newIndex = steps.stepIndex + 1;
    const stepsWithVal = steps.validatedAtCurrentStep(steps.inputs);
    if (stepsWithVal.validation.isSuccess()) {
      setSteps(
        stepsWithVal
          .withoutFirstValidationError()
          .withStepIndex(newIndex)
          .withRunValidationsOff()
      );
      if (newIndex < ANALYTICS_SCREENS.length) {
        analyticsScreenChangeEventWithInputs(
          ANALYTICS_SCREENS[newIndex],
          stepsWithVal.inputs
        );
      }
    } else {
      const firstError = getFirstValidationError(stepsWithVal.validation);
      pipe(
        firstError,
        fold(
          () => {
            setSteps(stepsWithVal.withRunValidationsOn());
          },
          (validationError) => {
            const stepsWithFirstError = stepsWithVal
              .withFirstValidationError(validationError)
              .withRunValidationsOn();
            setSteps(stepsWithFirstError);
          }
        )
      );
    }
  };

  const validateOnBlur = () => {
    // don't trigger validation unless contents of input field has ever changed
    if (steps.shouldRunValidations && dirty) {
      const v = validateStep1PremiumPayment(steps.inputs, steps.config);
      const combined = combineValidations([steps.validation, v]);
      setSteps(steps.withValidation(combined));
    }
  };

  const selectTerm = (term: TermLength) => {
    const newInputs = steps.inputs.withTerm(term);
    if (steps.shouldRunValidations && !steps.validation.isSuccess()) {
      const newValidatedSteps = steps.validatedAtCurrentStep(newInputs);
      setSteps(newValidatedSteps);
    } else {
      setSteps(steps.withInputs(newInputs));
    }
  };
  const deselectTerm = () => {
    const newInputs = steps.inputs.withoutTerm();
    if (steps.shouldRunValidations && !steps.validation.isSuccess()) {
      const newValidatedSteps = steps.validatedAtCurrentStep(newInputs);
      setSteps(newValidatedSteps);
    } else {
      setSteps(steps.withInputs(newInputs));
    }
  };
  const termLengthIssues = [
    VALIDATION_MUST_CHOOSE_INVESTMENT_TERM,
    VALIDATION_INVESTMENT_TERM_LENGTH_NOT_ALLOWED,
  ];

  const selectTermInvalid = steps.validation.hasIssueIn(termLengthIssues);

  const updateAmountEntered = (v: NumberFormatValues) => {
    setAmount(v.value);
    if (!dirty) {
      setDirty(true);
    }
    const stepsWithNewValue =
      v.value === ""
        ? steps.withInputs(steps.inputs.withoutPremiumPayment())
        : steps.withInputs(steps.inputs.withPremiumPayment(parseInt(v.value)));

    if (steps.shouldRunValidations && !steps.validation.isSuccess()) {
      const premiumValidation = validateStep1PremiumPayment(
        stepsWithNewValue.inputs,
        stepsWithNewValue.config
      );
      const combined = combineValidationsOnBlur(
        steps.validation,
        premiumValidation,
        [
          VALIDATION_MUST_ENTER_PREMIUM_PAYMENT,
          VALIDATION_PREMIUM_PAYMENT_OUT_OF_RANGE,
        ]
      );
      const stepsWithValidation = stepsWithNewValue.withValidation(combined);
      setSteps(stepsWithValidation);
    } else {
      setSteps(stepsWithNewValue);
    }
  };
  const premiumValidationMsgId = "rila-calculator-premium-validation-message";
  const termValidationMsgId = "rila-calculator-term-validation-message";

  const premiumDescribedBy = steps.validation.hasIssueIn(
    premiumValidationIssues
  )
    ? premiumValidationMsgId
    : premiumInputDescriptionId;

  return (
    <div id="input-section-1" className={inputStepClasses.join(" ")}>
      <div className="input-fields">
        <div className="step-header premium">
          <div className="label-header">
            <h3 id={premiumInputLabelId}>Premium</h3>
          </div>
          <div className="description">
            <p id={premiumInputDescriptionId} className="instruction">
              Enter an amount between {minValue} and {maxValue} to invest.
            </p>
          </div>
        </div>
        <div className="inputs step-1-inputs">
          <label htmlFor={premiumInputId} className="sr-only">
            Premium
          </label>
          <NumberFormat
            displayType="input"
            id={premiumInputId}
            getInputRef={(ref: HTMLInputElement) => {
              premiumRef = ref;
            }}
            prefix="$"
            thousandSeparator=","
            placeholder="Enter amount in dollars"
            size={12}
            maxLength={10}
            className="dollar-amount form-input"
            value={amount}
            aria-required={true}
            aria-invalid={steps.validation.hasIssueIn(premiumValidationIssues)}
            aria-describedby={premiumDescribedBy}
            isNumericString={true}
            isAllowed={(v) => v.value.match(/^\d*$/) !== null}
            onBlur={validateOnBlur}
            onValueChange={updateAmountEntered}
            autoComplete="off"
          />
          <ValidationMessage
            id={premiumValidationMsgId}
            messagesByIssue={premiumValidationMessages}
          />
        </div>
        <div className="step-header term">
          <div className="label-header">
            <h3>Term</h3>
          </div>
          <p id={termButtonsDescriptionId} className="instruction">
            Choose the index strategy term length.
          </p>
        </div>
        <div className="inputs step-1-inputs">
          <label id={termButtonsLabelId} className="sr-only">
            Premium
          </label>
          <SelectTermLength
            id={termButtonsId}
            labelId={termButtonsLabelId}
            descriptionId={termButtonsDescriptionId}
            validationMessageId={termValidationMsgId}
            term={steps.inputs.term}
            selectTerm={selectTerm}
            deselectTerm={deselectTerm}
            invalid={selectTermInvalid}
            getRef={termSelectGrabFocus}
          />

          <ValidationMessage
            id={termValidationMsgId}
            messagesByIssue={investmentTermValidationMessages}
          />
        </div>
      </div>
      <div className="next-button-row">
        <button type="button" className="btn btn-next" onClick={handleNext}>
          Next {nextIcon}
        </button>
      </div>
    </div>
  );
};
